import React, { useCallback, useEffect, useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BaseDialog from "../../components/utils/dialog/BaseDialog";

const ENABLED_VALUES = new Set(["1", "true", "yes", "on"]);

export const isInvitationFeatureEnabled = (value) =>
  ENABLED_VALUES.has(`${value || ""}`.trim().toLowerCase());

const readStoredAccess = ({ enabled, invitationKey, storageKey }) => {
  if (!enabled) return true;
  if (!invitationKey || !storageKey || typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(storageKey) === invitationKey;
  } catch (_error) {
    return false;
  }
};

export const useInvitationAccess = ({ enabled, invitationKey, storageKey }) => {
  const normalizedKey = `${invitationKey || ""}`.trim();
  const [hasAccess, setHasAccess] = useState(() =>
    readStoredAccess({
      enabled,
      invitationKey: normalizedKey,
      storageKey,
    }),
  );

  useEffect(() => {
    setHasAccess(
      readStoredAccess({
        enabled,
        invitationKey: normalizedKey,
        storageKey,
      }),
    );
  }, [enabled, normalizedKey, storageKey]);

  const acceptInvitation = useCallback(
    (candidateKey) => {
      if (!enabled) {
        setHasAccess(true);
        return { success: true };
      }

      if (!normalizedKey || !storageKey) {
        return {
          success: false,
          error:
            "Invitation access is not configured. Please contact the administrator.",
        };
      }

      if (`${candidateKey || ""}`.trim() !== normalizedKey) {
        return {
          success: false,
          error:
            "The invitation key is not valid. Please check it and try again.",
        };
      }

      try {
        window.localStorage.setItem(storageKey, normalizedKey);
      } catch (_error) {
        // Storage can be unavailable in private browsing. Keep access for this session.
      }

      setHasAccess(true);
      return { success: true };
    },
    [enabled, normalizedKey, storageKey],
  );

  return { acceptInvitation, hasAccess };
};

const InvitationAccessDialog = ({
  open,
  onClose,
  onSubmit,
  title = "Invitation-only access",
  subtitle = "This feature is currently available to invited users.",
  featureName = "This feature",
  description,
  helperText = "Use the invitation key shared with you by our team.",
  invitationLabel = "Invitation key",
  continueLabel = "Continue",
  cancelLabel = "Not now",
  icon = <LockOutlinedIcon sx={{ fontSize: 28 }} />,
}) => {
  const [invitationKey, setInvitationKey] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setInvitationKey("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!invitationKey.trim() || submitting) return;

    setError("");
    setSubmitting(true);

    try {
      const result = await onSubmit?.(invitationKey);
      if (!result?.success) {
        setError(
          result?.error || "Access could not be verified. Please try again.",
        );
      }
    } catch (submitError) {
      setError(
        submitError?.message ||
          "Access could not be verified. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      closeDisabled={submitting}
      title={title}
      subtitle={subtitle}
      icon={icon}
      maxWidth='sm'
      contentSx={{ p: { xs: 2, sm: 3 } }}
      paperSx={{ maxHeight: "calc(100vh - 24px)", overflow: "hidden" }}
      actionsSx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}
      actions={
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Button
            onClick={onClose}
            disabled={submitting}
            variant='outlined'
            fullWidth
            sx={{ textTransform: "none" }}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!invitationKey.trim() || submitting}
            variant='contained'
            fullWidth
            sx={{ textTransform: "none" }}
          >
            {submitting ? "Checking..." : continueLabel}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant='body1' fontWeight={700} gutterBottom>
            Limited preview
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {description ||
              `${featureName} is being tested with a small group before wider release.`}
          </Typography>
        </Box>

        <Alert severity='info' variant='outlined' sx={{ py: 0.25 }}>
          Your invitation key is only needed once on this device. You may be
          asked again when access is updated.
        </Alert>

        <TextField
          autoFocus
          fullWidth
          type='password'
          label={invitationLabel}
          value={invitationKey}
          error={Boolean(error)}
          helperText={error || helperText}
          onChange={(event) => {
            setInvitationKey(event.target.value);
            if (error) setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSubmit();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <VpnKeyOutlinedIcon color='action' fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </BaseDialog>
  );
};

export default InvitationAccessDialog;
